/**
 * `diagram/parse.ts` — mermaid flowchart-subset parser (interactive-diagrams-v0.1.md §5.1).
 *
 * Parses the *inner* content of a ` ```mermaid ` fence into a `DiagramModel`. Deliberately our
 * own parser, not mermaid's internal API (plan build note): mermaid's parser needs browser
 * globals (untestable in Node), the MCP layer needs an equivalent Python parser anyway, and
 * owning the grammar removes the internal-API churn risk (§8). Mermaid remains the *renderer*.
 *
 * **The safety property**: every source line is classified into a `Statement`; any line we
 * don't fully understand becomes a `raw` statement and round-trips verbatim. So a diagram
 * using constructs outside our editable subset still serializes byte-identically — we just
 * can't edit those constructs on the canvas. We never corrupt what we don't understand.
 *
 * Supported editable subset (v0.1):
 *   - Headers: `flowchart`/`graph` with optional direction (TB/TD/BT/RL/LR).
 *   - Nodes with shapes: `[]`, `()`, `([])`, `[[]]`, `[()]`, `(())`, `{}`, `{{}}`, `[//]`,
 *     `[\\]`, `[/\]`, `[\/]`, `>]`, plus quoted labels and `:::class` refs.
 *   - Edges: solid/dotted/thick × arrow/open/circle/cross/bidirectional, chained (`A-->B-->C`),
 *     with inline (`A-- x -->B`) or pipe (`A-->|x|B`) labels.
 *   - `subgraph … end` grouping (membership by node id).
 *   - Comments (`%%`), the `@aletheia` sidecar, and directive lines (`classDef`/`class`/
 *     `style`/`linkStyle`/`click`/`%%{…}%%`) — all preserved, not structurally edited.
 *
 * Anything else (other diagram types, exotic connectors) → the model is marked non-`editable`
 * and/or the line is a `raw` passthrough.
 */

import {
  type DiagramType,
  type DiagramNode,
  type DiagramEdge,
  type NodeShape,
  type Statement,
  type Subgraph,
  type ConnectorStyle,
  type EdgeArrow,
  type DiagramModel,
  type SidecarData,
  SHAPE_DELIMS,
  DEFAULT_DIRECTION,
  edgeKey,
} from './model';
import { parseSidecarLine, mergeSidecar, isSidecarLine } from './sidecar';

/** Valid flowchart direction tokens (TD is a mermaid alias for TB). */
const DIRECTIONS = new Set(['TB', 'TD', 'BT', 'RL', 'LR']);

/** Directive line prefixes we preserve opaquely. `style` is NOT here — it is parsed (v0.2). */
const DIRECTIVE_PREFIXES = ['classDef', 'class ', 'linkStyle', 'click ', 'direction '];

/**
 * A native node-style directive: `style <id> k:v,k:v,…`. Captures the id and the raw prop list.
 * Values may contain `#hex`, `px`, spaces after commas — we split on commas and the FIRST colon.
 */
const STYLE_RE = /^style\s+([A-Za-z0-9_]+)\s+(.+)$/;

/**
 * A `classDef <name>[,<name>…] <props>` legend definition. Names are comma-separated; the props
 * are the same `k:v,…` list as `style`. A trailing `;` (idiomatic in mermaid) is stripped before
 * the props are parsed. Kept as an opaque `directive` statement for serialization, but ALSO read
 * here so the class colors resolve onto nodes (see `effectiveNodeStyle`).
 */
const CLASSDEF_RE = /^classDef\s+([A-Za-z0-9_,]+)\s+(.+)$/;

/** A `class <id>[,<id>…] <name>` assignment line — attaches a legend class to existing nodes. */
const CLASS_ASSIGN_RE = /^class\s+([A-Za-z0-9_,]+)\s+([A-Za-z0-9_]+)$/;

/** Parse `fill:#f00,stroke-width:2px` into an ordered prop record. Unparseable pairs dropped. */
export function parseStyleProps(list: string): Record<string, string> {
  const props: Record<string, string> = {};
  for (const pair of list.split(',')) {
    const idx = pair.indexOf(':');
    if (idx < 1) continue;
    const key = pair.slice(0, idx).trim();
    const value = pair.slice(idx + 1).trim();
    if (key && value) props[key] = value;
  }
  return props;
}

/** Strip a single trailing CR so `\r\n` sources classify correctly; `raw` keeps the CR. */
function clean(line: string): string {
  return line.endsWith('\r') ? line.slice(0, -1) : line;
}

/** The leading-whitespace prefix of a line (used to keep sidecar indentation stable). */
export function indentOf(line: string): string {
  const m = /^\s*/.exec(line);
  return m ? m[0] : '';
}

// ─────────────────────────────────────────────────────────────────────────────
// Node-token scanning
// ─────────────────────────────────────────────────────────────────────────────

interface NodeMatch {
  node: DiagramNode;
  /** Number of characters consumed from the scan start. */
  length: number;
}

const ID_RE = /^[A-Za-z0-9_]+/;
const CLASS_RE = /^(?::::[A-Za-z0-9_]+)+/;

/**
 * Attempt to scan a node token at `slice` start: an id, an optional shape wrapper with label,
 * and optional `:::class` refs. Returns null if there is no id at the start.
 */
function scanNode(slice: string): NodeMatch | null {
  const idM = ID_RE.exec(slice);
  if (!idM) return null;
  const id = idM[0];
  let pos = id.length;
  let shape: NodeShape = 'none';
  let label = id;
  let quoted = false;

  const shapeM = scanShape(slice.slice(pos));
  if (shapeM) {
    shape = shapeM.shape;
    label = shapeM.label;
    quoted = shapeM.quoted;
    pos += shapeM.length;
  }

  const classes: string[] = [];
  const classM = CLASS_RE.exec(slice.slice(pos));
  if (classM) {
    for (const c of classM[0].split(':::')) if (c) classes.push(c);
    pos += classM[0].length;
  }

  return { node: { id, label, shape, quoted, classes }, length: pos };
}

interface ShapeMatch {
  shape: NodeShape;
  label: string;
  quoted: boolean;
  length: number;
}

/** Match a close delimiter against the text after an opener. `consumed` counts label + close. */
function matchClose(
  afterOpen: string,
  close: string,
): { label: string; quoted: boolean; consumed: number } | null {
  // Quoted label: read to the matching closing double-quote, then require the close delim.
  if (afterOpen.startsWith('"')) {
    const endQuote = afterOpen.indexOf('"', 1);
    if (endQuote === -1) return null;
    const rest = afterOpen.slice(endQuote + 1);
    if (!rest.startsWith(close)) return null;
    return { label: afterOpen.slice(1, endQuote), quoted: true, consumed: endQuote + 1 + close.length };
  }
  // Unquoted: read up to the first close-delimiter occurrence.
  const endIdx = afterOpen.indexOf(close);
  if (endIdx === -1) return null;
  return { label: afterOpen.slice(0, endIdx), quoted: false, consumed: endIdx + close.length };
}

/**
 * Scan a shape wrapper at `slice` start (the id has already been consumed). Openers are tried
 * longest-first (`SHAPE_DELIMS` order) so `([` beats `(` and `[[` beats `[`. For the matched
 * opener, shapes that SHARE it (e.g. `[/` → both parallelogram `/]` and trapezoid `\]`) are
 * disambiguated by the **nearest** close — otherwise a far-away delimiter later on the line
 * (a following node's `\]`) would be greedily swallowed. Returns null if unterminated.
 */
function scanShape(slice: string): ShapeMatch | null {
  const triedOpeners = new Set<string>();
  for (const { open } of SHAPE_DELIMS) {
    if (triedOpeners.has(open) || !slice.startsWith(open)) continue;
    triedOpeners.add(open);
    const afterOpen = slice.slice(open.length);
    // Among all shapes sharing this opener, pick the one whose close terminates earliest.
    let best: ShapeMatch | null = null;
    for (const cand of SHAPE_DELIMS) {
      if (cand.open !== open) continue;
      const m = matchClose(afterOpen, cand.close);
      if (m && (best === null || m.consumed < best.length - open.length)) {
        best = { shape: cand.shape, label: m.label, quoted: m.quoted, length: open.length + m.consumed };
      }
    }
    if (best) return best;
    // Opener matched but nothing closes it → fall through to a shorter opener.
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Link scanning
// ─────────────────────────────────────────────────────────────────────────────

interface LinkMatch {
  connector: ConnectorStyle;
  arrow: EdgeArrow;
  label?: string;
  length: number;
}

/**
 * Ordered link recognizers, tried against the remaining slice. Inline-labeled forms come
 * first (they require `\s+label\s+`, so they never false-match an unlabeled `-->`); then
 * plain connectors, each of which may carry a trailing `|label|`. Anything unrecognized
 * returns null → the whole statement falls back to `raw` (round-trip safe, non-editable).
 */
const INLINE_LINKS: Array<{ re: RegExp; connector: ConnectorStyle; arrow: EdgeArrow }> = [
  { re: /^={2,}\s+(.+?)\s+={2,}>/, connector: 'thick', arrow: 'arrow' },
  { re: /^={2,}\s+(.+?)\s+={2,}(?![=>])/, connector: 'thick', arrow: 'open' },
  { re: /^-\.\s+(.+?)\s+\.-+>/, connector: 'dotted', arrow: 'arrow' },
  { re: /^-\.\s+(.+?)\s+\.-+(?!>)/, connector: 'dotted', arrow: 'open' },
  { re: /^-{2,}\s+(.+?)\s+-{2,}>/, connector: 'solid', arrow: 'arrow' },
  { re: /^-{2,}\s+(.+?)\s+-{2,}o/, connector: 'solid', arrow: 'circle' },
  { re: /^-{2,}\s+(.+?)\s+-{2,}x/, connector: 'solid', arrow: 'cross' },
  { re: /^-{2,}\s+(.+?)\s+-{2,}(?![->ox])/, connector: 'solid', arrow: 'open' },
];

const PLAIN_LINKS: Array<{ re: RegExp; connector: ConnectorStyle; arrow: EdgeArrow }> = [
  { re: /^<-{2,}>/, connector: 'solid', arrow: 'bi' },
  { re: /^-\.-*>/, connector: 'dotted', arrow: 'arrow' },
  { re: /^-\.-+(?!>)/, connector: 'dotted', arrow: 'open' },
  { re: /^={2,}>/, connector: 'thick', arrow: 'arrow' },
  { re: /^={3,}(?!>)/, connector: 'thick', arrow: 'open' },
  { re: /^-{2,}o/, connector: 'solid', arrow: 'circle' },
  { re: /^-{2,}x/, connector: 'solid', arrow: 'cross' },
  { re: /^-{2,}>/, connector: 'solid', arrow: 'arrow' },
  { re: /^-{3,}(?![>ox])/, connector: 'solid', arrow: 'open' },
];

const PIPE_LABEL_RE = /^\|([^|]*)\|/;

/** Scan a link connector (with optional inline or pipe label) at `slice` start. */
function scanLink(slice: string): LinkMatch | null {
  for (const { re, connector, arrow } of INLINE_LINKS) {
    const m = re.exec(slice);
    if (m) return { connector, arrow, label: m[1], length: m[0].length };
  }
  for (const { re, connector, arrow } of PLAIN_LINKS) {
    const m = re.exec(slice);
    if (!m) continue;
    let length = m[0].length;
    let label: string | undefined;
    const pipeM = PIPE_LABEL_RE.exec(slice.slice(length));
    if (pipeM) {
      label = pipeM[1];
      length += pipeM[0].length;
    }
    return { connector, arrow, label, length };
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Chain parsing (`node (link node)*`)
// ─────────────────────────────────────────────────────────────────────────────

interface RawEdge {
  from: string;
  to: string;
  connector: ConnectorStyle;
  arrow: EdgeArrow;
  label?: string;
}

interface ChainResult {
  nodes: DiagramNode[];
  edges: RawEdge[];
}

/**
 * Parse a diagram body statement as a chain of node tokens joined by links. Returns the nodes
 * and the edges between consecutive nodes, or null if the text isn't a clean chain (leaving
 * the caller to keep it as a `raw` statement). A single node with no links yields
 * `{ nodes: [n], edges: [] }` — the caller treats that as a standalone node statement.
 */
function parseChain(text: string): ChainResult | null {
  let pos = 0;
  const skipWs = () => {
    while (pos < text.length && /\s/.test(text[pos])) pos += 1;
  };

  skipWs();
  const first = scanNode(text.slice(pos));
  if (!first) return null;
  pos += first.length;

  const nodes: DiagramNode[] = [first.node];
  const edges: RawEdge[] = [];
  let prev = first.node;

  for (;;) {
    skipWs();
    if (pos >= text.length) break;

    const link = scanLink(text.slice(pos));
    if (!link) return null; // trailing junk we don't understand → not a clean chain
    pos += link.length;

    skipWs();
    const next = scanNode(text.slice(pos));
    if (!next) return null;
    pos += next.length;

    nodes.push(next.node);
    edges.push({
      from: prev.id,
      to: next.node.id,
      connector: link.connector,
      arrow: link.arrow,
      label: link.label,
    });
    prev = next.node;
  }

  return { nodes, edges };
}

// ─────────────────────────────────────────────────────────────────────────────
// Line classification
// ─────────────────────────────────────────────────────────────────────────────

/** Parse a `subgraph …` header line into an id + optional title. */
function parseSubgraphHeader(rest: string): { id: string; title?: string } {
  const trimmed = rest.trim();
  if (trimmed === '') return { id: '_sg' };
  // `id [Title]` or `id ["Title"]`
  const bracket = /^([A-Za-z0-9_]+)\s*\[(.+)\]$/.exec(trimmed);
  if (bracket) {
    let title = bracket[2];
    if (title.startsWith('"') && title.endsWith('"')) title = title.slice(1, -1);
    return { id: bracket[1], title };
  }
  // `"Title"` (quoted, no id) → title is the id-less display name
  if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
    const title = trimmed.slice(1, -1);
    return { id: title, title };
  }
  // Bare token / phrase → serves as both id and title
  return { id: trimmed, title: trimmed };
}

/** True if the cleaned line is a directive we preserve opaquely. */
function isDirective(cleaned: string): boolean {
  const t = cleaned.trim();
  if (t.startsWith('%%{')) return true; // %%{init:…}%% config directive
  return DIRECTIVE_PREFIXES.some((p) => t.startsWith(p));
}

// ─────────────────────────────────────────────────────────────────────────────
// Top-level parse
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Parse the inner text of a mermaid fence into a `DiagramModel`. Never throws.
 *
 * @param source - The fence body (everything between the ` ```mermaid ` and closing ` ``` `).
 * @returns A `DiagramModel`. For non-flowchart/graph diagrams (or a missing header),
 *   `editable` is false and every line is preserved as a `raw`/`comment` statement so the
 *   source still round-trips; the caller renders those with vanilla mermaid.
 */
export function parse(source: string): DiagramModel {
  const lines = source.split('\n');
  const statements: Statement[] = [];

  // A leading `---` YAML frontmatter block (mermaid title/config) is consumed first: its lines are
  // preserved verbatim but skipped for header detection + node parsing, and `title:` is extracted.
  const { end: fmEnd, title } = detectFrontmatter(lines);

  // Locate the diagram header: first meaningful (non-blank, non-comment, non-directive) line
  // *after* any frontmatter.
  let headerIdx = -1;
  let diagramType: DiagramType = 'unknown';
  let direction = DEFAULT_DIRECTION;

  for (let i = fmEnd; i < lines.length; i += 1) {
    const c = clean(lines[i]).trim();
    if (c === '' || c.startsWith('%%')) continue;
    const hm = /^(flowchart|graph)\b\s*([A-Za-z]{2})?/.exec(c);
    if (hm) {
      headerIdx = i;
      diagramType = hm[1] as DiagramType;
      if (hm[2] && DIRECTIONS.has(hm[2])) direction = hm[2];
    }
    break; // first meaningful line decides; if it isn't a flowchart header, type stays unknown
  }

  const editable = diagramType === 'flowchart' || diagramType === 'graph';

  // Non-editable (other diagram type or no header): preserve every line opaquely.
  if (!editable) {
    for (let i = 0; i < lines.length; i += 1) {
      const line = lines[i];
      const cleaned = clean(line);
      if (i < fmEnd) {
        statements.push({ kind: 'frontmatter', raw: line });
      } else if (isSidecarLine(cleaned)) {
        statements.push({ kind: 'sidecar', raw: line, data: parseSidecarLine(cleaned) });
      } else if (cleaned.trim().startsWith('%%')) {
        statements.push({ kind: 'comment', raw: line });
      } else {
        statements.push({ kind: 'raw', raw: line });
      }
    }
    return finalize(statements, diagramType, direction, source, false, title);
  }

  // Editable flowchart: classify each line.
  for (let i = 0; i < lines.length; i += 1) {
    const raw = lines[i];
    const cleaned = clean(raw);
    const trimmed = cleaned.trim();

    if (i < fmEnd) {
      statements.push({ kind: 'frontmatter', raw });
      continue;
    }
    if (i === headerIdx) {
      statements.push({ kind: 'header', raw, diagramType, direction });
      continue;
    }
    if (trimmed === '') {
      statements.push({ kind: 'raw', raw });
      continue;
    }
    if (isSidecarLine(cleaned)) {
      statements.push({ kind: 'sidecar', raw, data: parseSidecarLine(cleaned) });
      continue;
    }
    if (trimmed.startsWith('%%')) {
      statements.push({ kind: 'comment', raw });
      continue;
    }
    const styleM = STYLE_RE.exec(trimmed);
    if (styleM) {
      statements.push({ kind: 'style', raw, id: styleM[1], props: parseStyleProps(styleM[2]) });
      continue;
    }
    if (isDirective(cleaned)) {
      statements.push({ kind: 'directive', raw });
      continue;
    }
    if (/^subgraph\b/.test(trimmed)) {
      const { id, title } = parseSubgraphHeader(trimmed.replace(/^subgraph\b/, ''));
      statements.push({ kind: 'subgraphStart', raw, id, title });
      continue;
    }
    if (trimmed === 'end') {
      statements.push({ kind: 'subgraphEnd', raw });
      continue;
    }

    // Node-or-edge statement. Strip a trailing `%% …` comment before structural parsing,
    // but only if the `%%` sits outside brackets/quotes (cheap heuristic: no unbalanced
    // bracket before it). For v0.1 we only strip a trailing comment when the `%%` is
    // preceded by whitespace and the segment before it parses as a clean chain.
    const chain = parseChain(trimmed);
    if (!chain) {
      statements.push({ kind: 'raw', raw });
      continue;
    }
    if (chain.edges.length === 0) {
      statements.push({ kind: 'node', raw, node: chain.nodes[0] });
    } else {
      statements.push({ kind: 'edge', raw, nodes: chain.nodes, edges: toDiagramEdges(chain) });
    }
  }

  return finalize(statements, diagramType, direction, source, true, title);
}

/**
 * Detect a leading `---` YAML frontmatter block (mermaid's title/config header). Only a block at
 * the very top counts. Returns the index of the first line *after* the block (0 if none) and the
 * `title:` value if present. An unterminated `---` is treated as content (not frontmatter).
 */
function detectFrontmatter(lines: string[]): { end: number; title?: string } {
  let i = 0;
  while (i < lines.length && lines[i].trim() === '') i += 1;
  if (i >= lines.length || lines[i].trim() !== '---') return { end: 0 };
  let title: string | undefined;
  let j = i + 1;
  for (; j < lines.length && lines[j].trim() !== '---'; j += 1) {
    const m = /^title\s*:\s*(.+?)\s*$/.exec(lines[j].trim());
    if (m && title === undefined) title = m[1].replace(/^['"]|['"]$/g, '').trim() || undefined;
  }
  if (j >= lines.length) return { end: 0 }; // no closing --- → not frontmatter
  return { end: j + 1, title };
}

/** Convert a chain's raw edges to `DiagramEdge`s (ordinals/keys are assigned in `finalize`). */
function toDiagramEdges(chain: ChainResult): DiagramEdge[] {
  return chain.edges.map((e) => ({
    from: e.from,
    to: e.to,
    ordinal: 0, // reassigned globally in finalize
    connector: e.connector,
    arrow: e.arrow,
    label: e.label,
    key: '', // reassigned in finalize
  }));
}

/**
 * Build the merged structured view (nodes/edges/subgraphs/sidecar) from the statement spine
 * and assemble the final `DiagramModel`. Edge ordinals+keys are assigned here, globally in
 * document order, so parallel edges disambiguate consistently (plan §3).
 */
function finalize(
  statements: Statement[],
  diagramType: DiagramType,
  direction: string,
  source: string,
  editable: boolean,
  title?: string,
): DiagramModel {
  const nodes = new Map<string, DiagramNode>();
  const edges: DiagramEdge[] = [];
  const subgraphs: Subgraph[] = [];
  const styles = new Map<string, Record<string, string>>();
  const classDefs = new Map<string, Record<string, string>>();
  const classAssignments = new Map<string, string[]>();
  let sidecar: SidecarData | null = null;

  const pairCounts = new Map<string, number>();
  const sgStack: Subgraph[] = [];

  const registerNode = (n: DiagramNode) => {
    const existing = nodes.get(n.id);
    // A shaped definition supersedes a bare-id reference; otherwise first-seen wins.
    if (!existing || (existing.shape === 'none' && n.shape !== 'none')) {
      nodes.set(n.id, n);
    }
    if (sgStack.length > 0) {
      const sg = sgStack[sgStack.length - 1];
      if (!sg.members.includes(n.id)) sg.members.push(n.id);
    }
  };

  for (const st of statements) {
    switch (st.kind) {
      case 'node':
        registerNode(st.node);
        break;
      case 'edge': {
        for (const n of st.nodes) registerNode(n);
        for (const e of st.edges) {
          const pk = `${e.from}->${e.to}`;
          const ordinal = pairCounts.get(pk) ?? 0;
          pairCounts.set(pk, ordinal + 1);
          e.ordinal = ordinal;
          e.key = edgeKey(e.from, e.to, ordinal);
          edges.push(e);
        }
        break;
      }
      case 'subgraphStart': {
        const sg: Subgraph = { id: st.id, title: st.title, members: [] };
        subgraphs.push(sg);
        sgStack.push(sg);
        break;
      }
      case 'subgraphEnd':
        sgStack.pop();
        break;
      case 'sidecar':
        sidecar = mergeSidecar(sidecar, st.data);
        break;
      case 'style':
        // Merge per-property, last directive wins (mermaid applies them in order too).
        styles.set(st.id, { ...(styles.get(st.id) ?? {}), ...st.props });
        break;
      case 'directive': {
        // classDef / class lines stay opaque for serialization but feed the color resolver.
        const t = st.raw.trim().replace(/;+\s*$/, '');
        const cd = CLASSDEF_RE.exec(t);
        if (cd) {
          const props = parseStyleProps(cd[2]);
          for (const name of cd[1].split(',')) {
            if (name) classDefs.set(name, { ...(classDefs.get(name) ?? {}), ...props });
          }
          break;
        }
        const ca = CLASS_ASSIGN_RE.exec(t);
        if (ca) {
          for (const nodeId of ca[1].split(',')) {
            if (!nodeId) continue;
            const list = classAssignments.get(nodeId) ?? [];
            if (!list.includes(ca[2])) list.push(ca[2]);
            classAssignments.set(nodeId, list);
          }
        }
        break;
      }
      default:
        break;
    }
  }

  return {
    diagramType,
    direction,
    statements,
    nodes,
    edges,
    subgraphs,
    styles,
    classDefs,
    classAssignments,
    sidecar,
    title,
    source,
    editable,
  };
}
